import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import PinterestIcon from '@mui/icons-material/Pinterest';
import TwitterIcon from '@mui/icons-material/Twitter';
import LanguageIcon from '@mui/icons-material/Language';
import ConfigCard from './ConfigCard';

// URL substring → icon mapping. Returns LanguageIcon as a generic fallback so
// every row always has a leading visual cue, even for niche networks.
const detectIcon = (url = '') => {
    const u = String(url).toLowerCase();
    if (u.includes('facebook'))  return FacebookIcon;
    if (u.includes('instagram')) return InstagramIcon;
    if (u.includes('youtube'))   return YouTubeIcon;
    if (u.includes('linkedin'))  return LinkedInIcon;
    if (u.includes('pinterest')) return PinterestIcon;
    if (u.includes('twitter') || u.includes('x.com')) return TwitterIcon;
    return LanguageIcon;
};

/**
 * "Social media links" card. Stand-alone so any modal that wants social
 * link management can include it directly.
 *
 * value: array of { id, url }
 * onChange(nextArray) — replaces the full list.
 */
const SocialMediaCard = ({ value = [], onChange, theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
    const list = Array.isArray(value) ? value : [];

    const update = (idx, patch) => {
        onChange && onChange(list.map((item, i) => i === idx ? { ...item, ...patch } : item));
    };
    const add = () => {
        onChange && onChange([...list, { id: `sm_${Date.now()}`, url: '' }]);
    };
    const remove = (idx) => {
        onChange && onChange(list.filter((_, i) => i !== idx));
    };

    return (
        <ConfigCard
            title={tr('cfg_footer_social_title', 'Social media links')}
            description={tr('cfg_footer_social_desc', 'Paste the full URL — the icon is auto-detected from the platform.')}
            theme={theme}
        >
            <Stack spacing={1}>
                {list.length === 0 && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                        {tr('cfg_footer_social_empty', 'No social profiles added yet.')}
                    </Typography>
                )}
                {list.map((item, idx) => {
                    const Icon = detectIcon(item.url);
                    return (
                        <Box key={item.id ?? idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Icon sx={{ color: 'text.secondary' }} />
                            <TextField
                                size="small"
                                fullWidth
                                value={item.url || ''}
                                onChange={(e) => update(idx, { url: e.target.value })}
                                placeholder="https://www.instagram.com/myshop"
                            />
                            <IconButton size="small" onClick={() => remove(idx)} title={tr('cfg_footer_social_remove', 'Remove link')}>
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    );
                })}
                <Box>
                    <Button size="small" startIcon={<AddIcon />} onClick={add} variant="outlined">
                        {tr('cfg_footer_social_add', 'Add social profile')}
                    </Button>
                </Box>
            </Stack>
        </ConfigCard>
    );
};

SocialMediaCard.propTypes = {
    value:    PropTypes.array,
    onChange: PropTypes.func,
    theme:    PropTypes.string,
    t:        PropTypes.func,
};

export default SocialMediaCard;
